/**
 * Single source of truth for API paths. Never hardcode a URL string inside
 * a service file — add it here and import it instead.
 */
export const endpoints = {
  auth: {
    login: "/auth/login",
    register: "/auth/register",       // Staff email+password path (RegisterDto)
    logout: "/auth/logout",
    refresh: "/auth/refresh",
    me: "/auth/me",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    changePassword: "/auth/change-password",
    profile: "/auth/profile",
    // Firebase Phone Auth (see bizpandit-api/src/auth/auth.controller.ts).
    // `sendOtp` is a pre-flight rate-limit call before the client SDK actually
    // sends the SMS; `verifyOtp` accepts the Firebase idToken plus intent
    // (LOGIN vs REGISTER).
    sendOtp: "/auth/send-otp",
    verifyOtp: "/auth/verify-otp",
  },
  // Client-portal self-signup — top-level POST /register (RegistrationController).
  // Creates User + Lead + ClientSignupRequest in one transaction; requires the
  // phone to have been OTP-verified via /auth/verify-otp?intent=REGISTER first.
  register: "/register",
  publicConfig: {
    firebase: "/public/firebase-config",
  },
  dashboard: {
    stats: "/dashboard/stats",
    revenue: "/dashboard/revenue",
    activity: "/dashboard/activity",
  },
  leads: {
    root: "/leads",
    byId: (id: string) => `/leads/${id}`,
    astrologyProfile: (leadId: string) => `/leads/${leadId}/astrology-profile`,
  },
  accounts: {
    root: "/accounts",
    byId: (id: string) => `/accounts/${id}`,
  },
  invoices: {
    root: "/invoices",
    byId: (id: string) => `/invoices/${id}`,
  },
  tasks: {
    root: "/tasks",
    byId: (id: string) => `/tasks/${id}`,
  },
  numerology: {
    numberAnalysis: "/numerology/number-analysis",
    businessNameAnalysis: "/numerology/business-name-analysis",
  },
  signupRequests: {
    root: "/admin/signup-requests",
    byId: (id: string) => `/admin/signup-requests/${id}`,
    approve: (id: string) => `/admin/signup-requests/${id}/approve`,
    reject: (id: string) => `/admin/signup-requests/${id}/reject`,
    requestMoreInfo: (id: string) => `/admin/signup-requests/${id}/request-more-info`,
  },
  packageApprovals: {
    root: "/admin/package-approvals",
    stats: "/admin/package-approvals/stats",
    byId: (id: string) => `/admin/package-approvals/${id}`,
    approve: (id: string) => `/admin/package-approvals/${id}/approve`,
    reject: (id: string) => `/admin/package-approvals/${id}/reject`,
    requestMoreInfo: (id: string) => `/admin/package-approvals/${id}/request-more-info`,
  },
  pricing: {
    discountTiers: "/pricing/discount-tiers",
    discountTierById: (id: string) => `/pricing/discount-tiers/${id}`,
    paymentSettings: "/pricing/payment-settings",
    gstMode: "/pricing/gst-mode",
  },
  lov: {
    root: "/lov",
    byId: (id: string) => `/lov/${id}`,
    values: (lovId: string) => `/lov/${lovId}/values`,
    valueById: (lovId: string, valueId: string) => `/lov/${lovId}/values/${valueId}`,
  },
  items: {
    root: "/items",
    byId: (id: string) => `/items/${id}`,
  },
  services: {
    root: "/services",
    byId: (id: string) => `/services/${id}`,
    steps: (serviceId: string) => `/services/${serviceId}/steps`,
    stepById: (serviceId: string, stepId: string) => `/services/${serviceId}/steps/${stepId}`,
  },
  packages: {
    root: "/packages",
    byId: (id: string) => `/packages/${id}`,
    sellables: (packageId: string) => `/packages/${packageId}/sellables`,
    sellableById: (packageId: string, sellableId: string) => `/packages/${packageId}/sellables/${sellableId}`,
    tranches: (packageId: string) => `/packages/${packageId}/tranches`,
    trancheById: (packageId: string, trancheId: string) => `/packages/${packageId}/tranches/${trancheId}`,
  },
  events: {
    root: "/events",
    byId: (id: string) => `/events/${id}`,
    highlightedPackages: (eventId: string) => `/events/${eventId}/highlighted-packages`,
    highlightedPackageById: (eventId: string, highlightedPackageId: string) =>
      `/events/${eventId}/highlighted-packages/${highlightedPackageId}`,
  },
  client: {
    dashboard: "/client/dashboard",
    profile: "/client/profile",
    community: "/client/community",
    communityById: (id: string) => `/client/community/${id}`,
    referrals: "/client/referrals",
    dreamAsks: "/client/referrals/asks",
    dreamAskById: (id: string) => `/client/referrals/asks/${id}`,
    dreamGives: "/client/referrals/gives",
    dreamGiveById: (id: string) => `/client/referrals/gives/${id}`,
  },
  payment: {
    createOrder: "/payment/create-order",
    verifyPayment: "/payment/verify-payment",
    cashOrder: "/payment/cash-order",
    history: "/payment/history",
    byId: (id: string) => `/payment/${id}`,
  },
  uploads: {
    paymentProof: "/uploads/payment-proof",
  },
} as const;
